import React, { useEffect } from 'react';
import { useAuth, useQuiz } from './hooks';
import { Login, Home, Quiz, Results } from './components';
import { STORAGE_KEYS } from './constants';
import './App.css';
import { useQuizStorage } from './hooks/useLocalStorage';

function App() {
  const { isAuthenticated, sla, login, logout } = useAuth();
  const {
    page,
    setPage,
    questions,
    currentQuestionIndex,
    questionLength,
    currentQuestionNum,
    isCorrect,
    wrongQuestions,
    setWrongQuestions,
    setQuestionLength,
    setQuestions,
    setCurrentQuestionIndex,
    setCurrentQuestionNum,
    handleStartQuiz,
    handleSubmitAnswer,
    handleNextQuestion,
  } = useQuiz();
  const { clearQuizData, saveQuizData } = useQuizStorage();

  // マウント時処理
  useEffect(() => {
    const checkCookie = () => {
      const cookies = document.cookie.split(';').map(cookie => cookie.trim());
      const userCookie = cookies.find(cookie => cookie.startsWith('userinfo='));
      
      if (!userCookie) {
        clearQuizData();
        setPage('login');
        return;
      }

      const value = userCookie.split('=')[1];
      if (['basic', 'exective'].includes(value)) {
        setWrongQuestions(JSON.parse(localStorage.getItem(STORAGE_KEYS.WRONG_QUESTIONS)));
        login(value === 'basic' ? 'basic' : 'exective', value);
        setPage('home');
      } else {
        clearQuizData();
        setPage('login');
      }
    };
    checkCookie();
  }, []);

  // 問題がLocalStorageにあるか確認
  function checkExistQuiz() {
    if (
      localStorage.getItem(STORAGE_KEYS.QUESTIONS) &&
      localStorage.getItem(STORAGE_KEYS.CURRENT_INDEX) &&
      localStorage.getItem(STORAGE_KEYS.LENGTH) &&
      localStorage.getItem(STORAGE_KEYS.CURRENT_NUM)
    ) {
      return true;
    } else {
      return false;
    }
  }

  // フリーアカウント
  const handleFreeAccess = () => {
    setPage('home');
    login('free', 'free');
  };

  // ライセンス認証
  const handleAuth = async (password) => {
    try {
      const response = await fetch(process.env.PUBLIC_URL + '/data/userinfo.json');
      const AUTHENTICATION_CODE = await response.json();

      const userInfo = AUTHENTICATION_CODE.find(
        info => info.authcode === password || info.execode === password
      );

      if (userInfo) {
        if (userInfo.authcode === password) {
          document.cookie = 'userinfo=basic; path=/; max-age=10800; Secure; SameSite=Strict;';
          setPage('home');
          login('basic', 'basic');
        } else {
          document.cookie = 'userinfo=exective; path=/; max-age=86400; Secure; SameSite=Strict;';
          setPage('home');
          login('exective', 'exective');
        }
      } else {
        clearQuizData();
        alert('認証コードが正しくありません。');
        setPage('login');
      }
    } catch (error) {
      console.error('Authentication failed:', error);
      alert('認証に失敗しました。');
    }
  };

  // 間違えた問題を再挑戦
  const restartQuiz = () => {
    if (window.confirm('中断中の問題は削除されますがよろしいですか？') && wrongQuestions.length > 0) {
      const randomNum = Math.round(Math.random() * (wrongQuestions.length - 1));

      // LocalStorageから問題を取得
      setQuestionLength(JSON.parse(localStorage.getItem(STORAGE_KEYS.WRONG_QUESTIONS)).length);
      setQuestions(JSON.parse(localStorage.getItem(STORAGE_KEYS.WRONG_QUESTIONS)));
      setCurrentQuestionIndex(randomNum);
      setCurrentQuestionNum(1);
      setWrongQuestions('');

      // LocalStorageの問題データを削除
      localStorage.removeItem(STORAGE_KEYS.QUESTIONS);
      localStorage.removeItem(STORAGE_KEYS.CURRENT_INDEX);
      localStorage.removeItem(STORAGE_KEYS.LENGTH);
      localStorage.removeItem(STORAGE_KEYS.CURRENT_NUM);
      localStorage.removeItem(STORAGE_KEYS.WRONG_QUESTIONS);

      // 問題データをlocalStorageに保存
      localStorage.setItem(STORAGE_KEYS.QUESTIONS, JSON.stringify(wrongQuestions));
      localStorage.setItem(STORAGE_KEYS.CURRENT_INDEX, randomNum);
      localStorage.setItem(STORAGE_KEYS.LENGTH, wrongQuestions.length);
      localStorage.setItem(STORAGE_KEYS.CURRENT_NUM, 1);

      setPage('quiz');
    }
  }

  // 続きから問題を開始
  const continueQuiz = () => {
    // LocalStorageから問題を取得
    setQuestionLength(localStorage.getItem(STORAGE_KEYS.LENGTH));
    setQuestions(JSON.parse(localStorage.getItem(STORAGE_KEYS.QUESTIONS)));
    setCurrentQuestionIndex(localStorage.getItem(STORAGE_KEYS.CURRENT_INDEX));
    setCurrentQuestionNum(Number(localStorage.getItem(STORAGE_KEYS.CURRENT_NUM)));

    // ページをセット
    setPage('quiz');
  }

  const handleLogout = () => {
    // 状態のリセット
    setQuestions([]);
    setCurrentQuestionIndex(0);
    setQuestionLength(0);
    setCurrentQuestionNum(0);
    setWrongQuestions([]);
    
    // ログアウト処理
    logout(setPage);
  };

  return (
    <div className="app-container">
      {page === 'login' && (
        <Login 
          onAuth={handleAuth} 
          onFreeAccess={handleFreeAccess} 
        />
      )}
      {page === 'home' && (
        <Home
          onStartQuiz={handleStartQuiz}
          onRestartQuiz={restartQuiz}
          onContinueQuiz={continueQuiz}
          wrongQuestions={wrongQuestions}
          sla={sla}
          hasExistingQuiz={checkExistQuiz()}
          onLogout={handleLogout}
        />
      )}
      {page === 'quiz' && <Quiz currentQuestionNum={currentQuestionNum} questionLength={questionLength} question={questions[currentQuestionIndex]} submitAnswers={handleSubmitAnswer}/>}
      {page === 'results' && <Results currentQuestionNum={currentQuestionNum} questionLength={questionLength} question={questions[currentQuestionIndex]} isCorrect={isCorrect} nextQuestion={handleNextQuestion} returnHome={() => setPage('home')}/>}
    </div>
  );
}

export default App;
